import type { AnchorHTMLAttributes } from 'react';
import { Link as RouterLink, useRouterState } from '@tanstack/react-router';

/**
 * The vendored Nexus layout was written against react-router (`Link to`,
 * `useLocation`). This module offers the same surface on top of TanStack
 * Router, so each vendored file only swaps its import line (NEXUS.md).
 */

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string };

export function Link({ to, children, ...rest }: LinkProps) {
  if (/^(https?:|mailto:)/.test(to) || to.startsWith('#')) {
    return (
      <a href={to} {...rest}>
        {children}
      </a>
    );
  }
  // Menu URLs are plain strings; the route tree checks them at runtime.
  // activeProps={{}}: TanStack adds an `active` class on prefix matches by default
  // (react-router's Link doesn't); the sidebar sets `active` itself, longest match only.
  return (
    <RouterLink to={to as never} activeProps={{}} {...rest}>
      {children}
    </RouterLink>
  );
}

export function useLocation() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return { pathname };
}
